// Settings that live in the browser, as the apps keep them per device (@AppStorage): which of Someday, Stale and the
// Archive Home shows (a Pro customization; without Pro Home shows them all), after how many days a to-do is stale,
// and per list whether it shows its completed to-dos and puts deadlines first (ListPresentation.swift:
// `showsCompletedKey`, `deadlinesFirstKey`), and the sound of ticking off (Setting.completionSound). Kept in
// localStorage, which may refuse (a private window): then the defaults hold for the visit. Whether Favorites is folded on
// Home is kept here too (Setting.favoritesFolded, docs/61): Favorites is not a record that could carry it. Whether done
// checklist items move below the open ones (Setting.checklistDoneLast), as the apps keep it per device.

import { useSyncExternalStore } from "react";
import { canShowCompleted, canSortDeadlinesFirst, canHideLater, storageKey, type CompletedShown, type ListArrangement, type Route } from "../core/lists";
import { WEEK_BANDS, type WeekBand } from "../core/week";

export const STALE_CHOICES = [14, 30, 60, 90] as const;

/** What one list remembers. An absent value is the list's default. */
export interface ListPref {
  showsCompleted?: boolean;
  deadlinesFirst?: boolean;
}

export interface Prefs {
  homeShows: { someday: boolean; stale: boolean; archive: boolean };
  staleAfterDays: number;
  /** Today as a day (the Day view, Pro) rather than a list. */
  todayShowsDay: boolean;
  /** Home hidden in a wide window: Today's or a project's overview beside the list (docs/51); off, the list alone. */
  showsOverview: boolean;
  /** By the list's storage key. */
  lists: Record<string, ListPref>;
  /**
   * Scheduled as a week (Setting.scheduledShowsWeek), "Unscheduled" under it unless folded, and its tab (docs/56); the
   * rows' details under the title (Setting.weekShowsDetails) and the band's height as dragged, 0 following the window
   * (Setting.weekBandHeight).
   */
  week: { shows: boolean; showsBand: boolean; band: WeekBand; showsDetails: boolean; bandHeight: number };
  /** A short, soft sound when a to-do is ticked off (Setting.completionSound, docs/59). On unless switched off. */
  completionSound: boolean;
  /** Favorites folded under their heading on Home (docs/61). Open unless folded. */
  favoritesFolded: boolean;
  /** Done checklist items are shown below the open ones (Setting.checklistDoneLast, core/checklist.ts). On unless switched off. */
  checklistDoneLast: boolean;
  /** Habits (docs/63): Today shows them in a group of their own (Setting.habitsInOwnGroup); off, among the to-dos. */
  habitsInOwnGroup: boolean;
  /** The streak's number on a habit's badge (Setting.showsStreaks); off, the flame alone. */
  showsStreaks: boolean;
  /** New habits start with Never Miss Twice on (Setting.neverMissTwiceDefault). */
  neverMissTwiceDefault: boolean;
  /** A card celebrates a habit's milestones (Setting.celebratesMilestones). */
  celebratesMilestones: boolean;
  /** Today keeps the user's sections when nothing is left in them (Setting.todayShowsEmptySections). Off unless switched on. */
  todayShowsEmptySections: boolean;
}

const KEY = "todolix.prefs";
const DEFAULTS: Prefs = {
  homeShows: { someday: true, stale: true, archive: true }, staleAfterDays: 30, todayShowsDay: false, showsOverview: false, lists: {},
  week: { shows: false, showsBand: true, band: "anytime", showsDetails: true, bandHeight: 0 },
  completionSound: true, favoritesFolded: false, checklistDoneLast: true,
  habitsInOwnGroup: false, showsStreaks: true, neverMissTwiceDefault: true, celebratesMilestones: true,
  todayShowsEmptySections: false,
};

function read(): Prefs {
  try {
    const stored = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Prefs> | null;
    if (!stored) return DEFAULTS;
    const days = STALE_CHOICES.includes(stored.staleAfterDays as (typeof STALE_CHOICES)[number]) ? stored.staleAfterDays! : DEFAULTS.staleAfterDays;
    const lists: Record<string, ListPref> = {};
    for (const [key, value] of Object.entries(stored.lists ?? {})) {
      if (typeof value !== "object" || value === null) continue;
      const pref: ListPref = {};
      if (typeof value.showsCompleted === "boolean") pref.showsCompleted = value.showsCompleted;
      if (typeof value.deadlinesFirst === "boolean") pref.deadlinesFirst = value.deadlinesFirst;
      lists[key] = pref;
    }
    const week = { ...DEFAULTS.week };
    if (typeof stored.week?.shows === "boolean") week.shows = stored.week.shows;
    if (typeof stored.week?.showsBand === "boolean") week.showsBand = stored.week.showsBand;
    if (WEEK_BANDS.includes(stored.week?.band as WeekBand)) week.band = stored.week!.band;
    if (typeof stored.week?.showsDetails === "boolean") week.showsDetails = stored.week.showsDetails;
    if (typeof stored.week?.bandHeight === "number" && stored.week.bandHeight >= 0) week.bandHeight = stored.week.bandHeight;
    const completionSound = typeof stored.completionSound === "boolean" ? stored.completionSound : DEFAULTS.completionSound;
    const favoritesFolded = typeof stored.favoritesFolded === "boolean" ? stored.favoritesFolded : DEFAULTS.favoritesFolded;
    const checklistDoneLast = typeof stored.checklistDoneLast === "boolean" ? stored.checklistDoneLast : DEFAULTS.checklistDoneLast;
    const flag = (key: "habitsInOwnGroup" | "showsStreaks" | "neverMissTwiceDefault" | "celebratesMilestones" | "todayShowsEmptySections") =>
      (typeof stored[key] === "boolean" ? stored[key] : DEFAULTS[key]);
    return {
      homeShows: { ...DEFAULTS.homeShows, ...stored.homeShows }, staleAfterDays: days, todayShowsDay: stored.todayShowsDay === true,
      showsOverview: stored.showsOverview === true, lists, week, completionSound, favoritesFolded, checklistDoneLast,
      habitsInOwnGroup: flag("habitsInOwnGroup"), showsStreaks: flag("showsStreaks"), neverMissTwiceDefault: flag("neverMissTwiceDefault"),
      celebratesMilestones: flag("celebratesMilestones"), todayShowsEmptySections: flag("todayShowsEmptySections"),
    };
  } catch {
    return DEFAULTS;
  }
}

let current: Prefs = typeof localStorage === "undefined" ? DEFAULTS : read();
const listeners = new Set<() => void>();

export function prefs(): Prefs {
  return current;
}

export function setPrefs(change: (prefs: Prefs) => Prefs): void {
  current = change(current);
  try {
    localStorage.setItem(KEY, JSON.stringify(current));
  } catch {
    // Kept for this visit only.
  }
  for (const listener of [...listeners]) listener();
}

export function subscribePrefs(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function usePrefs(): Prefs {
  return useSyncExternalStore(subscribePrefs, prefs, prefs);
}

// MARK: - Per list

export function setListPref(route: Route, change: ListPref): void {
  const key = storageKey(route);
  setPrefs((all) => ({ ...all, lists: { ...all.lists, [key]: { ...all.lists[key], ...change } } }));
}

/** Whether the list shows its completed to-dos: Today does unless hidden, a project once asked (Route.showsCompletedByDefault). */
export function showsCompleted(route: Route, all: Prefs = current): boolean {
  if (!canShowCompleted(route)) return false;
  return all.lists[storageKey(route)]?.showsCompleted ?? route.kind === "today";
}

/** Which completed to-dos the list shows now: Today those done today, a project all of them, elsewhere none (CompletedShown.init). */
export function completedShown(route: Route, all: Prefs = current): CompletedShown {
  if (!showsCompleted(route, all)) return "none";
  return completedKind(route);
}

/** Which completed to-dos a list shows when it shows them (Route.completedKind). */
export function completedKind(route: Route): CompletedShown {
  return route.kind === "today" ? "today" : "all";
}

export function deadlinesFirst(route: Route, all: Prefs = current): boolean {
  return canSortDeadlinesFirst(route) && (all.lists[storageKey(route)]?.deadlinesFirst ?? false);
}

/** How the list arranges its to-dos with the browser's settings (ListArrangement.init(route:options:)). */
export function arrangementFor(route: Route, all: Prefs = current): ListArrangement {
  return { deadlinesFirst: deadlinesFirst(route, all), laterLast: canHideLater(route) };
}

// MARK: - The week

export function setWeekPref(change: Partial<Prefs["week"]>): void {
  setPrefs((all) => ({ ...all, week: { ...all.week, ...change } }));
}
